import { useState, useCallback } from "react";
import apiService from "../../../../service/Api";
import { Testimonial } from "../types/testimonials";

interface UseTestimonialsReturn {
  testimonials: Testimonial[];
  loading: boolean;
  error: string | null;
  fetchTestimonials: () => Promise<void>;
  saveTestimonial: (
    testimonial: Omit<Testimonial, "id" | "created_at">,
  ) => Promise<Testimonial>;
  updateTestimonial: (
    id: string,
    updates: Partial<Testimonial>,
  ) => Promise<void>;
  deleteTestimonial: (id: string) => Promise<void>;
}

export const useTestimonials = (): UseTestimonialsReturn => {
  const [testimonials, setTestimonials] = useState<Testimonial[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchTestimonials = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await apiService.get<Testimonial[]>(
        "/api/configurations/testimonials/",
      );
      setTestimonials(response.data);
    } catch (err: unknown) {
      setError(
        (err as { message?: string })?.message ||
          "Failed to fetch testimonials",
      );
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const saveTestimonial = useCallback(
    async (
      testimonial: Omit<Testimonial, "id" | "created_at">,
    ): Promise<Testimonial> => {
      try {
        const { data } = await apiService.post<Testimonial>(
          "/api/configurations/testimonials/",
          testimonial,
        );
        setTestimonials((prev) => [...prev, data]);
        return data;
      } catch (err: unknown) {
        setError(
          (err as { message?: string })?.message ||
            "Failed to save testimonials",
        );
        throw err;
      }
    },
    [],
  );

  const updateTestimonial = useCallback(
    async (id: string, updates: Partial<Testimonial>) => {
      try {
        const { data } = await apiService.put<Testimonial>(
          `/api/configurations/testimonials/${id}/`,
          updates,
        );
        setTestimonials((prev) => prev.map((t) => (t.id === id ? data : t)));
      } catch (err: unknown) {
        setError(
          (err as { message?: string })?.message ||
            "Failed to update testimonials",
        );
        throw err;
      }
    },
    [],
  );

  const deleteTestimonial = useCallback(async (id: string) => {
    try {
      await apiService.delete(`/api/configurations/testimonials/${id}/`);
      setTestimonials((prev) => prev.filter((t) => t.id !== id));
    } catch (err: unknown) {
      setError(
        (err as { message?: string })?.message ||
          "Failed to delete testimonials",
      );
      throw err;
    }
  }, []);

  return {
    testimonials,
    loading,
    error,
    fetchTestimonials,
    saveTestimonial,
    updateTestimonial,
    deleteTestimonial,
  };
};
